import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import { getUserData } from './utils/userService';
import { useLandingPageCleanup } from './contexts/LandingPageContext';

const HiddenPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isCleaningUp, cleanupComplete } = useLandingPageCleanup();
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    // URL'den secret key kontrolü (opsiyonel - daha güvenli için)
    const searchParams = new URLSearchParams(location.search);
    const secretKey = searchParams.get('key');
    
    // Secret key kontrolü - isterseniz bu kısmı kaldırabilir veya değiştirebilirsiniz
    const REQUIRED_SECRET_KEY = 'ss12'; // Buraya kendi secret key'inizi yazın
    
    // Auth kontrolü
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        try {
          const data = await getUserData(currentUser.uid);
          setUserData(data);
          
          // İsteğe bağlı: Sadece premium kullanıcılar veya belirli kullanıcılar erişebilir
          // Örnek: Sadece premium kullanıcılar
          // if (data?.isPremium) {
          //   setAuthorized(true);
          // }
          
          // Veya secret key kontrolü
          if (secretKey === REQUIRED_SECRET_KEY) {
            setAuthorized(true);
          } else {
            // Secret key yoksa veya yanlışsa ana sayfaya yönlendir
            navigate('/');
          }
        } catch (error) {
          console.error('Kullanıcı verisi alınırken hata:', error);
          navigate('/');
        }
      } else {
        // Giriş yapılmamışsa ana sayfaya yönlendir
        navigate('/');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, location]);

  // CSS cleanup tamamlanana kadar loading göster
  if (isCleaningUp || !cleanupComplete) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-gray-900 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-gray-900 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return null; // Veya bir hata mesajı gösterebilirsiniz
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full p-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-8 text-center">
            Yaşasın Hamileyim Yol Haritası
          </h1>
          
          {/* Flowchart Container */}
          <div className="flowchart-container" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2rem',
            padding: '2rem 0'
          }}>
            
            {/* Satış Kanalları - Ana Kutu */}
            <div className="flowchart-node" style={{
              position: 'relative',
              backgroundColor: '#7c3aed',
              color: 'white',
              padding: '1.5rem 3rem',
              borderRadius: '12px',
              fontSize: '1.25rem',
              fontWeight: '600',
              boxShadow: '0 4px 15px rgba(124, 58, 237, 0.3)',
              minWidth: '250px',
              textAlign: 'center'
            }}>
              Satış Kanalları
            </div>

            {/* Alt Elementler - Yan Yana (3 element) */}
            <div style={{
              display: 'flex',
              gap: '2rem',
              position: 'relative',
              flexWrap: 'wrap',
              justifyContent: 'center',
              marginTop: '3rem'
            }}>
              {/* Yatay Çizgi - 3 element için */}
              <div style={{
                position: 'absolute',
                left: '0',
                right: '0',
                top: '-3rem',
                height: '3px',
                backgroundColor: '#7c3aed'
              }}></div>

              {/* Sosyal Medya */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="flowchart-node" style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '1.25rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                  minWidth: '200px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  Sosyal Medya
                  {/* Üstten bağlantı çizgisi */}
                  <div style={{
                    position: 'absolute',
                    top: '-3rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '3px',
                    height: '3rem',
                    backgroundColor: '#7c3aed'
                  }}></div>
                </div>

                {/* Sosyal Medya altındaki bağlantı çizgisi */}
                <div style={{
                  width: '3px',
                  height: '3rem',
                  backgroundColor: '#10b981',
                  position: 'relative',
                  marginTop: '0.5rem'
                }}></div>

                {/* Organik Paylaşım */}
                <div className="flowchart-node" style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '1rem 1.5rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                  minWidth: '180px',
                  textAlign: 'center',
                  border: '3px solid #f59e0b'
                }}>
                  Organik Paylaşım
                </div>
              </div>

              {/* Web Sitesi */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="flowchart-node" style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '1.25rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                  minWidth: '200px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  Web Sitesi
                  {/* Üstten bağlantı çizgisi */}
                  <div style={{
                    position: 'absolute',
                    top: '-3rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '3px',
                    height: '3rem',
                    backgroundColor: '#7c3aed'
                  }}></div>
                </div>

                {/* Web Sitesi altındaki bağlantı çizgisi */}
                <div style={{
                  width: '3px',
                  height: '3rem',
                  backgroundColor: '#10b981',
                  position: 'relative',
                  marginTop: '0.5rem'
                }}></div>

                {/* SEO */}
                <div className="flowchart-node" style={{
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  padding: '1rem 1.5rem',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                  minWidth: '180px',
                  textAlign: 'center'
                }}>
                  SEO
                </div>
              </div>

              {/* Reklam */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="flowchart-node" style={{
                  backgroundColor: '#10b981',
                  color: 'white',
                  padding: '1.25rem 2rem',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)',
                  minWidth: '200px',
                  textAlign: 'center',
                  position: 'relative'
                }}>
                  Reklam
                  {/* Üstten bağlantı çizgisi */}
                  <div style={{
                    position: 'absolute',
                    top: '-3rem',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '3px',
                    height: '3rem',
                    backgroundColor: '#7c3aed'
                  }}></div>
                </div>

                {/* Reklam altındaki bağlantı çizgisi */}
                <div style={{
                  width: '3px',
                  height: '3rem',
                  backgroundColor: '#10b981',
                  position: 'relative',
                  marginTop: '0.5rem'
                }}></div>

                {/* Reklam alt elementleri - Yan yana */}
                <div style={{
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  position: 'relative',
                  marginTop: '0'
                }}>
                  {/* Yatay çizgi - Reklam'dan 4 elemente */}
                  <div style={{
                    position: 'absolute',
                    left: '0',
                    right: '0',
                    top: '0',
                    height: '3px',
                    backgroundColor: '#10b981'
                  }}></div>

                  {/* Instagram */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Üstten bağlantı çizgisi */}
                    <div style={{
                      width: '3px',
                      height: '3rem',
                      backgroundColor: '#10b981',
                      position: 'relative',
                      marginTop: '3px'
                    }}></div>
                    <div className="flowchart-node" style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '0.875rem 1.25rem',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      minWidth: '140px',
                      textAlign: 'center',
                      border: '3px solid #f59e0b'
                    }}>
                      Instagram
                    </div>
                    
                    {/* Instagram alt maddeler */}
                    <div style={{
                      marginTop: '1rem',
                      textAlign: 'left',
                      width: '100%',
                      maxWidth: '200px'
                    }}>
                      <ul style={{
                        listStyle: 'disc',
                        paddingLeft: '1.5rem',
                        margin: 0,
                        color: '#000000'
                      }}>
                        <li style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>Post seçimi</li>
                        <li style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>Uygun reklam çeşidi</li>
                        <li style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>Web UX</li>
                        <li style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>Doğru Fiyat</li>
                      </ul>
                    </div>
                  </div>

                  {/* YouTube */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Üstten bağlantı çizgisi */}
                    <div style={{
                      width: '3px',
                      height: '3rem',
                      backgroundColor: '#10b981',
                      position: 'relative',
                      marginTop: '3px'
                    }}></div>
                    <div className="flowchart-node" style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '0.875rem 1.25rem',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      minWidth: '140px',
                      textAlign: 'center'
                    }}>
                      YouTube
                    </div>
                  </div>

                  {/* Google AdWords */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Üstten bağlantı çizgisi */}
                    <div style={{
                      width: '3px',
                      height: '3rem',
                      backgroundColor: '#10b981',
                      position: 'relative',
                      marginTop: '3px'
                    }}></div>
                    <div className="flowchart-node" style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '0.875rem 1.25rem',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      minWidth: '140px',
                      textAlign: 'center'
                    }}>
                      Google AdWords
                    </div>
                  </div>

                  {/* Remarketing */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    {/* Üstten bağlantı çizgisi */}
                    <div style={{
                      width: '3px',
                      height: '3rem',
                      backgroundColor: '#10b981',
                      position: 'relative',
                      marginTop: '3px'
                    }}></div>
                    <div className="flowchart-node" style={{
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      padding: '0.875rem 1.25rem',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      fontWeight: '600',
                      boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                      minWidth: '140px',
                      textAlign: 'center',
                      border: '3px solid #f59e0b'
                    }}>
                      ReMarketing
                    </div>
                    
                    {/* Remarketing alt maddeler */}
                    <div style={{
                      marginTop: '1rem',
                      textAlign: 'left',
                      width: '100%',
                      maxWidth: '200px'
                    }}>
                      <ul style={{
                        listStyle: 'disc',
                        paddingLeft: '1.5rem',
                        margin: 0,
                        color: '#000000'
                      }}>
                        <li style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>Meta pixel</li>
                        <li style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>Retarget post seçimi</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default HiddenPage;

